/**
 * Recherche prédictive — module « tracking », sur le modèle `faq_search` /
 * `specs_search` (`assets/product-faq.js`, `assets/product-specs-search.js`).
 *
 * Responsabilité unique : le jeu d'events dataLayer. Il ne fait ni réseau, ni
 * ARIA, ni stockage — il LIT ce que les cinq autres modules ont déjà produit
 * (DOM du panneau, état du fetcher/des états, options ARIA du combobox) et ne
 * modifie jamais le panneau lui-même.
 *
 * S'enregistre sous `window.MintPredictiveSearch.tracking`, chargé en
 * `<script defer>` APRÈS les cinq autres — il lit `host.combobox` et
 * `host.states`, montés en dernier par `predictive-search.js`, qui monte donc
 * celui-ci en tout dernier des six.
 *
 * ── Pourquoi un sixième fichier plutôt que du code dans l'hôte ──────────────
 * `predictive-search.js` tient le cycle de vie et rien d'autre, et il est déjà
 * à son plafond (T22). Chaque responsabilité neuve reste révocable seule :
 * retirer ce fichier laisse un panneau qui cherche, navigue et se ferme, sans
 * jamais écrire dans `window.dataLayer`. `create()` rend `null` si le markup
 * manque, comme les cinq autres.
 *
 * ── Les 3 events (design doc §10), et pourquoi 3 et pas 4 ───────────────────
 * Le v1 ne poussait `search` qu'à la soumission, ce qui rend les trois
 * métriques du design doc incalculables : une recherche prédictive réussie se
 * termine par un clic sur une suggestion, pas par une soumission.
 *   1. `search` — à chaque requête ABOUTIE (pas chaque frappe), debounce de
 *      tracking 800 ms distinct du debounce fonctionnel 250 ms du fetcher,
 *      minimum 3 caractères normalisés. Constantes reprises telles quelles de
 *      `product-faq.js` / `product-specs-search.js` : convention de recherche
 *      interne du thème.
 *   2. `search_suggestion_click` — sur toute suggestion cliquée, état A compris
 *      (`search_term: ''` alors, et volontairement AUCUN `search` associé :
 *      créer un 4e event pour ce cas contredirait la règle « réutiliser plutôt
 *      que créer un quasi-doublon » de `.claude/rules/data-layer-tracking.md`).
 *   3. `search_panel_close` — seulement si au moins un `search` est parti
 *      pendant cette ouverture : un focus suivi d'une fermeture sans frappe
 *      n'est pas une recherche abandonnée.
 * `search_no_results` n'existe pas : c'est `search` avec `results_count: 0`
 * (décision déjà actée pour la FAQ, reprise ici).
 *
 * ── `search` : pourquoi une relecture bornée, pas une lecture unique ────────
 * 800 ms après la dernière frappe laisse le temps au fetch (250 ms + réseau)
 * de répondre, sans le garantir. Une lecture unique du DOM à l'aveugle
 * daterait `results_count` d'une requête déjà remplacée si le réseau traîne :
 * la lecture se rejoue jusqu'à ce que `states.state.loading` retombe à
 * `false` (même principe que l'anti-pattern n°23 — relecture bornée d'un état
 * asynchrone plutôt qu'une lecture unique prise trop tôt), bornée pour ne
 * jamais tourner indéfiniment sur un panneau fermé ou détruit.
 * `states.state.hasResults` distingue ENSUITE une requête aboutie (panneau
 * inséré, même avec 0 résultat affiché) d'une requête ABANDONNÉE — terme vidé
 * ou erreur réseau (`fail()` de `predictive-search-states.js`) : dans ces deux
 * cas `hasResults` reste `false`, et aucun `search` ne part. Sans ce garde, une
 * erreur réseau se lirait comme « recherche sans résultat », polluant la
 * métrique d'échec de la métrique d'abandon.
 *
 * ── `results_count_*` : compté dans le DOM, jamais transmis par le réseau ───
 * `predictive-search-panel.liquid` ne rend pas de compteur par type : les trois
 * chiffres sont comptés depuis `[data-suggestion-type="product|collection|
 * article"]` dans `host.results`, seul noeud qui porte l'état B. `top_category`
 * / `top_brand` / `useful_link` / `recent` (état A et bandeau des récentes) n'y
 * comptent jamais : ce ne sont pas des résultats de la requête en cours.
 *
 * ── `search_suggestion_click` : `ecommerce` omis hors carte produit ─────────
 * Seule `predictive-product-item.liquid` porte `data-item-id` : c'est le seul
 * `suggestion_type` où GA4 a un article à décrire. Un clic catégorie, article,
 * récente, état A ou le bouton « voir tous les résultats » n'est pas la
 * sélection d'un produit — la clé `ecommerce` est alors OMISE, jamais envoyée
 * avec un `items` vide (même convention que le `price` FMD : omettre, jamais
 * fabriquer). Le bouton « voir tous les résultats » n'a pas de
 * `data-suggestion-type` (posé par aucune des tâches précédentes, et hors
 * périmètre de celle-ci de le lui ajouter) : il se repère à son propre
 * attribut `[data-predictive-search-button]`, et son `suggestion_type` retombe
 * sur le littéral `'view_all'`.
 * `view_item_list` est pousssé séparément, à l'INSERTION d'une réponse qui
 * porte des produits (`handleResultsMutation`), pas au clic : c'est
 * l'impression de la liste, indépendante de toute interaction.
 *
 * ── Pas de verrou anti-doublon 768-989 px (anti-pattern n°17) ───────────────
 * Le panneau existe jusqu'à 3 fois par page (header desktop, header mobile,
 * drawer), et l'anti-pattern n°17 documente qu'un event de VUE posé à deux
 * emplacements part en double sur cette plage, où deux markups coexistent.
 * Aucun event de ce fichier n'est un event de vue passive : les trois sont
 * déclenchés par une INTERACTION (frappe, clic, fermeture) sur UNE instance
 * précise, et un `click`/`input` sur une instance ne bubble jamais vers une
 * autre — deux `<predictive-search>` sont des frères, pas des ancêtres l'un de
 * l'autre. Un même geste de l'utilisateur ne peut donc jamais produire deux
 * pushs. `view_item_list`, lui, est borné à l'instance dont le `results` a
 * effectivement changé (l'observateur est posé sur le noeud, pas sur le
 * document) : même garantie.
 *
 * Chargé en asset séparé, jamais en bloc `javascript` de section : raison
 * écrite en tête de `predictive-search.js`.
 */
(function (window, document) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  var TRACK_DEBOUNCE_MS = 800;
  var TRACK_MIN_LENGTH = 3;
  var TRACK_SETTLE_INTERVAL_MS = 150;
  var TRACK_SETTLE_MAX_ATTEMPTS = 10;

  function pushEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  // Accents strippés pour le seul test de longueur — patron product-faq.js /
  // product-specs-search.js (aucune fonction globale réutilisable dans le thème).
  function normalize(str) {
    var normalized = String(str == null ? '' : str).toLowerCase();
    try {
      normalized = normalized.normalize('NFD').replace(/[̀-ͯ]/g, '');
    } catch (error) {
      // Navigateur sans String.prototype.normalize : la longueur reste comparable,
      // seuls les accents pèsent un caractère de trop — sans conséquence ici.
    }
    return normalized;
  }

  function countByType(container, type) {
    return container ? container.querySelectorAll('[data-suggestion-type="' + type + '"]').length : 0;
  }

  // Libellé affiché, dans l'ordre : posé explicitement (récentes, T26), nom
  // ecommerce (produits), titre visuel (catégories/articles), texte brut (état
  // A — T14 — et bouton « voir tous les résultats », qui ne porte aucun des deux).
  function suggestionLabel(target) {
    var label = target.getAttribute('data-suggestion-label') || target.getAttribute('data-item-name');
    if (!label) {
      var titleNode = target.querySelector('.predictive-item__title');
      label = titleNode ? titleNode.textContent : target.textContent;
    }
    return String(label || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Rang 1-based dans la liste d'options ARIA du combobox (T22) : seul rang
  // tenu à jour, tous types confondus (récentes, état A, catégories/articles/
  // produits), dans l'ordre du DOM — `predictive-search-combobox.js`,
  // `OPTION_SELECTOR`. Repli sur `data-index` (rang dans la seule boucle
  // produits) si le combobox n'a pas indexé ce noeud — cas du bouton
  // « voir tous les résultats », qui ne porte pas `data-suggestion-type`.
  function suggestionPosition(host, target) {
    if (host.combobox && host.combobox.state && host.combobox.state.options) {
      var index = host.combobox.state.options.indexOf(target);
      if (index !== -1) return index + 1;
    }
    var dataIndex = parseInt(target.getAttribute('data-index'), 10);
    return isNaN(dataIndex) ? null : dataIndex;
  }

  /**
   * `ecommerce` conforme GA4 (Q-T4) : cf. le commentaire d'en-tête. FMD
   * (V4-01) : `data-price` est OMIS par la carte en parcours partenaire —
   * jamais envoyé à `0`, jamais reconstitué ici.
   */
  function productEcommerceFromNode(target) {
    var itemId = target.getAttribute('data-item-id');
    if (!itemId) return undefined;

    var item = { item_id: itemId, item_name: target.getAttribute('data-item-name') || '' };
    var brand = target.getAttribute('data-item-brand');
    if (brand) item.item_brand = brand;
    var index = target.getAttribute('data-index');
    if (index) item.index = parseInt(index, 10);
    var price = target.getAttribute('data-price');
    if (price) item.price = parseFloat(price);

    return { item_list_id: 'predictive_search', item_list_name: 'predictive_search', items: [item] };
  }

  /** GA4 `view_item_list` : un item par carte produit rendue dans la réponse. */
  function productListEcommerce(nodes) {
    var items = [];
    nodes.forEach(function (node, index) {
      var itemId = node.getAttribute('data-item-id');
      if (!itemId) return;

      var declaredIndex = parseInt(node.getAttribute('data-index'), 10);
      var item = {
        item_id: itemId,
        item_name: node.getAttribute('data-item-name') || '',
        index: isNaN(declaredIndex) ? index + 1 : declaredIndex,
      };
      var brand = node.getAttribute('data-item-brand');
      if (brand) item.item_brand = brand;
      var price = node.getAttribute('data-price');
      if (price) item.price = parseFloat(price);
      items.push(item);
    });
    return { item_list_id: 'predictive_search', item_list_name: 'predictive_search', items: items };
  }

  /**
   * @param {{ host: object }} options - `host` est l'élément `<predictive-search>`,
   *   qui porte déjà ses nœuds résolus et ses collaborateurs.
   * @returns {object|null}
   */
  function createTracking(options) {
    var config = options || {};
    var host = config.host;
    if (!host || !host.input || !host.results) return null;

    var input = host.input;
    var results = host.results;

    var state = {
      timer: null,
      /** Dernier terme et compte réellement poussés dans un `search`, relus par
       * `search_panel_close` — jamais recalculés depuis un DOM qui a pu changer
       * entre-temps (input vidé après la dernière recherche, par exemple). */
      term: '',
      resultsCount: 0,
      /** Une recherche a-t-elle abouti depuis la dernière ouverture ? Un focus
       * suivi d'une fermeture sans frappe n'est pas une recherche abandonnée. */
      searchPushed: false,
      hadClick: false,
      listeners: [],
      /** Signature (`item_id` joints) de la dernière liste d'impressions poussée. Garde
       *  contre le retour arrière, qui re-sert la MÊME liste depuis le cache client :
       *  re-voir une liste inchangée n'est pas une nouvelle impression. */
      dernieresImpressions: '',
    };

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      state.listeners.push({ target: target, type: type, handler: handler });
    }

    function flushSearch(raw, attempt) {
      if (host.states && host.states.state.loading && attempt < TRACK_SETTLE_MAX_ATTEMPTS) {
        state.timer = setTimeout(function () {
          flushSearch(raw, attempt + 1);
        }, TRACK_SETTLE_INTERVAL_MS);
        return;
      }
      // Requête abandonnée (terme vidé) ou en échec (`fail()`) : `hasResults`
      // reste `false` dans les deux cas, et ni l'une ni l'autre n'est une
      // recherche ABOUTIE. Absent (états mal montés) : on se contente du DOM.
      if (host.states && !host.states.state.hasResults) return;

      var resultsCountProducts = countByType(results, 'product');
      var resultsCountCollections = countByType(results, 'collection');
      /**
       * Vaut 0 par construction depuis le 2026-09-16 : le bloc « Articles et guides » a été
       * retiré du panneau, et `article` sorti de `RESOURCE_TYPES` — le type n'est donc ni
       * demandé au serveur ni rendu. La dimension est conservée pour ne pas casser la
       * continuité des rapports GA4 déjà construits dessus ; une métrique plate est attendue,
       * ce n'est pas une panne de collecte. La retirer, ou la rétablir, se décide côté
       * analytics, pas ici.
       */
      var resultsCountArticles = countByType(results, 'article');
      var resultsCount = resultsCountProducts + resultsCountCollections + resultsCountArticles;

      state.term = raw;
      state.resultsCount = resultsCount;
      state.searchPushed = true;

      pushEvent('search', {
        search_term: raw,
        results_count: resultsCount,
        results_count_products: resultsCountProducts,
        results_count_collections: resultsCountCollections,
        results_count_articles: resultsCountArticles,
        search_location: host.location,
      });

      pousserImpressions(results);
    }

    /**
     * `view_item_list` à la cadence de `search`, et PAS à chaque réponse réseau.
     *
     * Mesuré le 2026-09-16 (review Shopify) : l'ancien mécanisme suivait un `MutationObserver`
     * sur le nœud de résultats, donc chaque réponse ET chaque service du cache. « cannondale »
     * puis trois caractères espacés a produit **4 pushs × 5 items = 20 impressions** pour une
     * seule intention de recherche. À cadence humaine, le facteur estimé est de 3 à 8.
     * Conséquence : le CTR de la liste `predictive_search` (`select_item` ÷ `view_item_list`)
     * sort divisé d'autant, faux pendant des mois et **irrattrapable** — GA4 ne redédoublonne pas.
     *
     * Ici l'impression suit `search` : même debounce de 800 ms, même garde `hasResults`, donc
     * une impression par recherche aboutie. La garde d'identité couvre en plus le retour arrière,
     * qui re-sert la même liste depuis le cache sans que ce soit une nouvelle impression.
     */
    function pousserImpressions(noeud) {
      var produits = noeud.querySelectorAll('[data-suggestion-type="product"]');
      if (!produits.length) return;

      var signature = [];
      for (var i = 0; i < produits.length; i++) {
        signature.push(produits[i].getAttribute('data-item-id') || '');
      }
      var cle = signature.join('|');
      if (cle === state.dernieresImpressions) return;
      state.dernieresImpressions = cle;

      pushEvent('view_item_list', { ecommerce: productListEcommerce(produits) });
    }

    function handleInput(event) {
      if (event && event.isTrusted === false) return;
      clearTimeout(state.timer);

      var raw = input.value.trim();
      if (normalize(raw).length < TRACK_MIN_LENGTH) return;

      state.timer = setTimeout(function () {
        flushSearch(raw, 0);
      }, TRACK_DEBOUNCE_MS);
    }

    /**
     * Un seul écouteur délégué : `[data-suggestion-type]` couvre les quatre
     * producteurs existants (état A T14, catégories/articles T13, carte
     * produit T9, récentes T26) ; le bouton « voir tous les résultats » n'en
     * porte pas et se repère à son propre attribut.
     */
    function handleClick(event) {
      var target =
        event.target && typeof event.target.closest === 'function'
          ? event.target.closest('[data-suggestion-type], [data-predictive-search-button]')
          : null;
      if (!target || !host.contains(target)) return;

      state.hadClick = true;

      /**
       * `search_term` vide en ETAT A, et c'est une correction (recette T50, 2026-09-16).
       * `input.value` seul mentait sur `/search?q=cannondale`, ou le champ est PRE-REMPLI
       * cote serveur (`value="{{ search.terms … }}"`) : un clic sur un lien de l'etat A —
       * top categorie, top marque, lien utile — y etait attribue au terme de l'URL alors
       * qu'AUCUNE recherche n'avait ete faite dans le panneau. La metrique melangeait donc
       * « a cliqué une entrée de decouverte » et « a cherché cannondale puis cliqué ».
       * `state.hasResults` distingue les deux sans perdre le terme : il ne passe a `true`
       * qu'une fois un panneau de resultats insere. On ne peut pas se rabattre sur
       * `state.term` seul — il n'est pose qu'apres le debounce de tracking (800 ms), donc
       * un clic rapide en etat B perdrait le terme.
       */
      var enEtatB = !!(host.states && host.states.state && host.states.state.hasResults);

      var payload = {
        search_term: enEtatB ? input.value.trim() : '',
        suggestion_type: target.getAttribute('data-suggestion-type') || 'view_all',
        suggestion_position: suggestionPosition(host, target),
        suggestion_label: suggestionLabel(target),
        search_location: host.location,
      };
      var ecommerce = productEcommerceFromNode(target);
      if (ecommerce) payload.ecommerce = ecommerce;

      pushEvent('search_suggestion_click', payload);
    }

    function handleOpen() {
      state.searchPushed = false;
      state.hadClick = false;
    }

    /**
     * Le debounce de tracking en cours est coupé : une fermeture met fin à
     * l'intention, un `search` qui sortirait après daterait un panneau déjà
     * refermé.
     */
    function handleClose() {
      clearTimeout(state.timer);
      if (!state.searchPushed) return;

      pushEvent('search_panel_close', {
        search_term: state.term,
        results_count: state.resultsCount,
        had_click: state.hadClick,
        search_location: host.location,
      });
    }

    function destroy() {
      clearTimeout(state.timer);
      state.timer = null;
      state.dernieresImpressions = '';
      state.listeners.forEach(function (entry) {
        entry.target.removeEventListener(entry.type, entry.handler);
      });
      state.listeners = [];
    }

    listen(input, 'input', handleInput);
    listen(host, 'click', handleClick);
    listen(host, 'predictive-search:open', handleOpen);
    listen(host, 'predictive-search:close', handleClose);
    return { state: state, destroy: destroy };
  }

  registry.tracking = { create: createTracking };
})(window, document);
